import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { AnalisisIA } from './AnalisisIA.js';
import { CleaverResultado } from './CleaverResultado.js';
import { ExportarResultadosModal, type TipoExamen } from './ExportarResultadosModal.js';
import { KostickResultado } from './KostickResultado.js';
import { TermanResultado } from './TermanResultado.js';
import type {
  PuntajesKostick,
  PuntajesLifoEstilo,
  PuntajesTerman,
  ResultadoDetalle,
  ResultadoExamen,
  ResultadoLifoDetalle,
} from './types.js';

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '18px 22px',
        marginBottom: 16,
      }}
    >
      <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--heading)', marginBottom: 16 }}>{title}</h2>
      {children}
    </div>
  );
}

// ── Gráfica y tablas LIFO ─────────────────────────────────────────────────────

const LIFO_ESTILOS: Array<{ key: keyof PuntajesLifoEstilo; label: string }> = [
  { key: 'DA_AP', label: 'DA/AP' },
  { key: 'TM_CT', label: 'TM/CT' },
  { key: 'MT_CS', label: 'MT/CS' },
  { key: 'AD_NG', label: 'AD/NG' },
];

function totalDe(valores: PuntajesLifoEstilo): number {
  return valores.DA_AP + valores.TM_CT + valores.MT_CS + valores.AD_NG;
}

// Polígono único — condición Favorable del candidato.
function LifoFavorableRadar({ puntajes }: { puntajes: ResultadoLifoDetalle['puntajes_json'] }) {
  const data = LIFO_ESTILOS.map(({ key, label }) => ({ estilo: label, Candidato: puntajes.favorable[key] }));

  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadarChart data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="estilo" tick={{ fontSize: 13, fontWeight: 700 }} />
        <PolarRadiusAxis angle={90} domain={[0, 40]} tick={{ fontSize: 10 }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
        <Radar name="Candidato" dataKey="Candidato" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.35} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// Polígono único — condición Desfavorable del candidato.
function LifoDesfavorableRadar({ puntajes }: { puntajes: ResultadoLifoDetalle['puntajes_json'] }) {
  const data = LIFO_ESTILOS.map(({ key, label }) => ({ estilo: label, Candidato: puntajes.desfavorable[key] }));

  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadarChart data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="estilo" tick={{ fontSize: 13, fontWeight: 700 }} />
        <PolarRadiusAxis angle={90} domain={[0, 40]} tick={{ fontSize: 10 }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
        <Radar name="Candidato" dataKey="Candidato" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.35} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// Dos polígonos superpuestos — Candidato vs Perfil Ideal (Compatibilidad Puesto).
function LifoCompatibilidadRadar({ puntajes }: { puntajes: ResultadoLifoDetalle['puntajes_json'] }) {
  const { candidato, perfil_ideal } = puntajes.compatibilidad_puesto;
  const data = LIFO_ESTILOS.map(({ key, label }) => ({
    estilo: label,
    Candidato: candidato[key],
    'Perfil Ideal': perfil_ideal[key],
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <RadarChart data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="estilo" tick={{ fontSize: 13, fontWeight: 700 }} />
        <PolarRadiusAxis angle={90} domain={[0, 40]} tick={{ fontSize: 10 }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Radar name="Candidato" dataKey="Candidato" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
        <Radar name="Perfil Ideal" dataKey="Perfil Ideal" stroke="#10b981" fill="#10b981" fillOpacity={0.25} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// Compatibilidad Puesto — solo Favorable (Desfavorable no tiene perfil ideal
// aplicado, confirmado contra el Excel original — ver lifo_master_logic.md §7).
function LifoCompatibilidadTable({ puntajes }: { puntajes: ResultadoLifoDetalle['puntajes_json'] }) {
  const { candidato, perfil_ideal, diferencia } = puntajes.compatibilidad_puesto;

  return (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <table className="main-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Compatibilidad Puesto (Favorable)</th>
            {LIFO_ESTILOS.map(({ key, label }) => <th key={key}>{label}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ fontWeight: 600 }}>Candidato</td>
            {LIFO_ESTILOS.map(({ key }) => <td key={key}>{candidato[key]}</td>)}
            <td style={{ fontWeight: 600 }}>{totalDe(candidato)}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: 600 }}>Perfil ideal</td>
            {LIFO_ESTILOS.map(({ key }) => <td key={key}>{perfil_ideal[key]}</td>)}
            <td style={{ fontWeight: 600 }}>{totalDe(perfil_ideal)}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: 600 }}>Diferencia</td>
            {LIFO_ESTILOS.map(({ key }) => <td key={key}>{diferencia[key]}</td>)}
            <td style={{ fontWeight: 600 }}>{totalDe(diferencia)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LifoDesfavorableTable({ puntajes }: { puntajes: ResultadoLifoDetalle['puntajes_json'] }) {
  return (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <table className="main-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Desfavorable (sin comparación)</th>
            {LIFO_ESTILOS.map(({ key, label }) => <th key={key}>{label}</th>)}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ fontWeight: 600 }}>Candidato</td>
            {LIFO_ESTILOS.map(({ key }) => <td key={key}>{puntajes.desfavorable[key]}</td>)}
            <td style={{ fontWeight: 600 }}>{totalDe(puntajes.desfavorable)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Perfil ideal LIFO editable (BH-17) ────────────────────────────────────────
// Solo Vero/root llegan a esta página (ruta protegida por ESCRITURA_PSICO en
// el backend), así que no se requiere un chequeo de permiso adicional aquí.
// Los 4 valores son libres — sin validar que sumen 95.
function LifoPerfilIdealForm({
  aspiranteId,
  perfilIdeal,
}: {
  aspiranteId: string;
  perfilIdeal: PuntajesLifoEstilo;
}) {
  const { patch } = useApi();
  const queryClient = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [valores, setValores] = useState(perfilIdeal);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      patch<{ resultado: ResultadoLifoDetalle | null }>(
        `/psicometricos/aspirantes/${aspiranteId}/perfil-ideal-lifo`,
        valores,
      ),
    onSuccess: (data) => {
      setError(null);
      setAbierto(false);
      if (data.resultado) {
        queryClient.setQueryData(['aspirante-resultado', aspiranteId, 'lifo'], data.resultado);
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'No fue posible guardar el perfil ideal');
    },
  });

  if (!abierto) {
    return (
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setAbierto(true)}>
        Editar perfil ideal
      </button>
    );
  }

  return (
    <div style={{ marginTop: 16, padding: 14, border: '1px solid var(--border)', borderRadius: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {LIFO_ESTILOS.map(({ key, label }) => (
          <label key={key} className="field" style={{ flex: '1 1 100px' }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</span>
            <input
              type="number"
              value={valores[key]}
              onChange={(e) =>
                setValores((v) => ({ ...v, [key]: e.target.value === '' ? 0 : Number(e.target.value) }))
              }
            />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setAbierto(false)} disabled={mutation.isPending}>
          Cancelar
        </button>
        {error && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</span>}
      </div>
    </div>
  );
}

// ── Resultado por examen ──────────────────────────────────────────────────────

// Un 404 del backend significa "el candidato aún no termina ese examen".
function useResultadoExamen<T>(aspiranteId: string, tipoExamen: TipoExamen) {
  const { get } = useApi();
  const query = useQuery({
    queryKey: ['aspirante-resultado', aspiranteId, tipoExamen],
    queryFn: () =>
      get<T>(`/psicometricos/aspirantes/${aspiranteId}/resultado?tipo_examen=${tipoExamen}`),
    retry: false,
  });
  const sinResultado = query.error instanceof ApiError && query.error.status === 404;
  // Cualquier otro error (500, red) se muestra aparte para no confundirlo con "no lo ha hecho".
  const fallo = query.isError && !sinResultado;
  return { data: query.data, isLoading: query.isLoading, sinResultado, fallo };
}

function AvisoSect({ titulo, mensaje, esError = false }: { titulo: string; mensaje: string; esError?: boolean }) {
  return (
    <SectionCard title={titulo}>
      <div
        style={{
          textAlign: 'center',
          padding: '24px 0',
          color: esError ? 'var(--danger)' : 'var(--muted)',
          fontSize: 13,
        }}
      >
        {mensaje}
      </div>
    </SectionCard>
  );
}

// Sección de un examen sin resultado que pintar: aún no lo completa, o falló la carga.
function SinResultadoSect({ titulo, examen, fallo }: { titulo: string; examen: string; fallo: boolean }) {
  return fallo ? (
    <AvisoSect titulo={titulo} mensaje={`No se pudo cargar el resultado del ${examen}.`} esError />
  ) : (
    <AvisoSect titulo={titulo} mensaje={`Aún no disponible — el candidato no ha completado el ${examen}.`} />
  );
}

// ── Notas ─────────────────────────────────────────────────────────────────────

function NotasSection({
  aspiranteId,
  tipoExamen,
  notasIniciales,
  titulo = 'Notas',
}: {
  aspiranteId: string;
  tipoExamen: 'cleaver' | 'lifo';
  notasIniciales: string | null;
  titulo?: string;
}) {
  const { patch } = useApi();
  const queryClient = useQueryClient();
  const [notas, setNotas] = useState(notasIniciales ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      patch(`/psicometricos/aspirantes/${aspiranteId}/notas`, {
        tipo_examen: tipoExamen,
        notas: notas.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 2500);
      queryClient.invalidateQueries({ queryKey: ['aspirante-resultado', aspiranteId, tipoExamen] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'No fue posible guardar las notas');
    },
  });

  return (
    <SectionCard title={titulo}>
      <div className="field">
        <textarea
          rows={5}
          placeholder="Observaciones, contexto o comentarios sobre el candidato…"
          value={notas}
          onChange={(e) => {
            setNotas(e.target.value);
            setSaved(false);
          }}
          style={{ resize: 'vertical', minHeight: 90 }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar notas'}
        </button>
        {saved && <span style={{ color: 'var(--brand)', fontSize: 12 }}>✓ Guardadas</span>}
        {error && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</span>}
      </div>
    </SectionCard>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

export function AspiranteDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [exportarAbierto, setExportarAbierto] = useState(false);
  const cleaver = useResultadoExamen<ResultadoDetalle>(id!, 'cleaver');
  const lifo = useResultadoExamen<ResultadoLifoDetalle>(id!, 'lifo');
  const kostick = useResultadoExamen<ResultadoExamen<PuntajesKostick>>(id!, 'kostick');
  const terman = useResultadoExamen<ResultadoExamen<PuntajesTerman>>(id!, 'terman');
  const examenes = [cleaver, lifo, kostick, terman];

  const resultado = cleaver.data;
  const resultadoLifo = lifo.data;
  const cargando = examenes.some((e) => e.isLoading);
  // Se puede pintar la página si cada examen respondió con resultado o con 404.
  const hayRespuesta = examenes.some((e) => e.data || e.sinResultado);
  const aspirante = examenes.find((e) => e.data)?.data?.aspirante;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/aspirantes')}
        style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <BackIcon /> Volver a Aspirantes
      </button>

      {cargando && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>Cargando…</div>
      )}

      {!cargando && !hayRespuesta && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--danger)' }}>
          No se pudo cargar la información del aspirante.
        </div>
      )}

      {!cargando && hayRespuesta && (
        <>
          {/* Header info */}
          <div
            style={{
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '18px 22px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 3 }}>
                {aspirante?.nombre_completo ?? 'Aspirante'}
              </h1>
              {aspirante && (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {aspirante.puesto_aplicado} · {aspirante.email}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {resultado && (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Calificado: {resultado.calculado_en.slice(0, 10).split('-').reverse().join('/')}
                </div>
              )}
              {aspirante && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExportarAbierto(true)}>
                    Exportar resultados
                  </button>
                  <AnalisisIA aspiranteId={id!} nombreAspirante={aspirante.nombre_completo} />
                </>
              )}
            </div>
          </div>

          {aspirante && (
            <ExportarResultadosModal
              open={exportarAbierto}
              onClose={() => setExportarAbierto(false)}
              aspiranteId={id!}
              nombreAspirante={aspirante.nombre_completo}
              disponibles={{
                cleaver: !!cleaver.data,
                lifo: !!lifo.data,
                kostick: !!kostick.data,
                terman: !!terman.data,
              }}
            />
          )}

          {/* Cleaver */}
          {cleaver.sinResultado || cleaver.fallo ? (
            <SinResultadoSect titulo="Cleaver — DISC" examen="Cleaver" fallo={cleaver.fallo} />
          ) : resultado ? (
            <SectionCard title="Cleaver — DISC">
              <CleaverResultado puntajes={resultado.puntajes_json} />
            </SectionCard>
          ) : null}

          {/* LIFO */}
          {lifo.sinResultado || lifo.fallo ? (
            <SinResultadoSect titulo="LIFO" examen="LIFO" fallo={lifo.fallo} />
          ) : resultadoLifo ? (
            <SectionCard title="LIFO — Favorable vs Desfavorable">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 }}>
                Condiciones Favorables
              </h3>
              <LifoFavorableRadar puntajes={resultadoLifo.puntajes_json} />

              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', margin: '20px 0 4px' }}>
                Condiciones Desfavorables
              </h3>
              <LifoDesfavorableRadar puntajes={resultadoLifo.puntajes_json} />
              <LifoDesfavorableTable puntajes={resultadoLifo.puntajes_json} />

              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', margin: '20px 0 4px' }}>
                Compatibilidad Puesto — Candidato vs Perfil Ideal
              </h3>
              <LifoCompatibilidadRadar puntajes={resultadoLifo.puntajes_json} />
              <LifoCompatibilidadTable puntajes={resultadoLifo.puntajes_json} />
              <LifoPerfilIdealForm
                aspiranteId={id!}
                perfilIdeal={resultadoLifo.puntajes_json.compatibilidad_puesto.perfil_ideal}
              />
            </SectionCard>
          ) : null}

          {/* Kostick */}
          {kostick.sinResultado || kostick.fallo ? (
            <SinResultadoSect titulo="Kostick" examen="Kostick" fallo={kostick.fallo} />
          ) : kostick.data ? (
            <SectionCard title="Kostick (PAPI) — Roles y Necesidades">
              <KostickResultado puntajes={kostick.data.puntajes_json} />
            </SectionCard>
          ) : null}

          {/* Terman-Merrill */}
          {terman.sinResultado || terman.fallo ? (
            <SinResultadoSect titulo="Terman-Merrill" examen="Terman-Merrill" fallo={terman.fallo} />
          ) : terman.data ? (
            <SectionCard title="Terman-Merrill — Perfil por subprueba">
              <TermanResultado puntajes={terman.data.puntajes_json} />
            </SectionCard>
          ) : null}

          {/* Notas — solo si hay resultado del examen correspondiente */}
          {resultado && (
            <NotasSection
              aspiranteId={id!}
              tipoExamen="cleaver"
              titulo="Notas — Cleaver"
              notasIniciales={resultado.notas}
            />
          )}
          {resultadoLifo && (
            <NotasSection
              aspiranteId={id!}
              tipoExamen="lifo"
              titulo="Notas — LIFO"
              notasIniciales={resultadoLifo.notas}
            />
          )}
        </>
      )}
    </div>
  );
}
